import type { Folder } from '../redux/slices/lists/foldersSlice';
import type { FolderPath } from 'api/shared/folderPath';

// The folder-path wire type lives in api/shared (produced by one backend
// serializer across every folder-bearing resource). Re-exported here so
// existing consumers can keep importing it alongside these display helpers.
export type { FolderPath };

/**
 * Render a `folder_path` array as a display string ("Parent / Child / Leaf").
 * Names only — the zone is usually already its own column. `null`/empty → '—'.
 */
export const formatFolderPath = (path: FolderPath | null | undefined): string => (path && path.length ? path.map((e) => e.name).join(' / ') : '—');

/**
 * Shared, display-only "Folder" table column for the nap-backend #1076
 * top-level-object list pages. Renders the server-supplied `folder_path`;
 * non-sortable (the backend exposes no folder sort/filter). Append it to a
 * page's `columns` array (before the actions column).
 */
export const folderColumn = {
  id: 'folder_path',
  header: 'Folder',
  enableSorting: false,
  cell: ({ row }: { row: { original: { folder_path?: FolderPath | null } } }): string => formatFolderPath(row.original.folder_path),
};

// Minimal structural shape needed to resolve a folder's path. Both the full
// `Folder` (foldersSlice) and the narrower folder LOV summary satisfy it.
interface FolderNode {
  readonly folder_id: number;
  readonly name: string;
  readonly parent_folder_id: number | null;
}

/**
 * Map each folder in `list` to its root→leaf display path ("Parent / Child"),
 * resolving parents within the same list. Folders are zone-scoped, so a folder
 * and all its ancestors share a zone — filter `list` to a zone before calling
 * and the chains stay intact. Separator " / " matches {@link formatFolderPath}.
 */
export function folderPathsById(list: readonly FolderNode[]): Map<number, string> {
  const byId = new Map(list.map((f) => [f.folder_id, f]));
  const cache = new Map<number, string>();

  function getPath(f: FolderNode): string {
    const cached = cache.get(f.folder_id);
    if (cached !== undefined) return cached;
    let parentPath = '';
    if (f.parent_folder_id != null) {
      const parent = byId.get(f.parent_folder_id);
      if (parent) parentPath = getPath(parent);
    }
    const path = parentPath ? `${parentPath} / ${f.name}` : f.name;
    cache.set(f.folder_id, path);
    return path;
  }

  const out = new Map<number, string>();
  for (const f of list) out.set(f.folder_id, getPath(f));
  return out;
}

export interface FolderIdPathOption {
  readonly folder_id: number;
  readonly zone_name: string | null;
  readonly path: string;
}

/**
 * Like `buildFolderPathOptions`, but keyed by `folder_id` (the wire field the
 * #1076 objects send) rather than the path string (node-definitions' contract).
 * Filtered to a single zone when `zoneId` is set. The label is cosmetic since
 * selection is by id.
 */
export function buildFolderIdPathOptions(folders: readonly Folder[], zoneId?: number | null): FolderIdPathOption[] {
  const list = zoneId != null ? folders.filter((f) => f.zone_id === zoneId) : [...(folders || [])];
  const paths = folderPathsById(list);

  return list
    .map((f) => ({
      folder_id: f.folder_id,
      zone_name: f.zone_name,
      path: paths.get(f.folder_id) ?? f.name,
    }))
    .sort((a, b) => a.path.localeCompare(b.path));
}

/**
 * Build dropdown options of folder paths from a flat folder list.
 * Path format: "TopLevel/Subfolder/Leaf". Paths are built per zone when zoneName is set.
 */
export function buildFolderPathOptions(folders: readonly Folder[], zoneName?: string | null): { value: string; label: string }[] {
  const list = zoneName ? folders.filter((f) => f.zone_name === zoneName) : [...(folders || [])];
  const byId = new Map(list.map((f) => [f.folder_id, f]));
  const pathCache = new Map<number, string>();

  function getPath(f: Folder): string {
    const cached = pathCache.get(f.folder_id);
    if (cached !== undefined) return cached;
    let parentPath = '';
    if (f.parent_folder_id != null) {
      const parent = byId.get(f.parent_folder_id);
      if (parent) parentPath = getPath(parent);
    }
    const path = parentPath ? `${parentPath}/${f.name}` : f.name;
    pathCache.set(f.folder_id, path);
    return path;
  }

  const paths = list.map((f) => {
    const path = getPath(f);
    return { value: path, label: path };
  });
  const seen = new Set<string>();
  const unique = paths.filter((p) => {
    if (seen.has(p.value)) return false;
    seen.add(p.value);
    return true;
  });
  unique.sort((a, b) => a.value.localeCompare(b.value));
  return [{ value: '', label: 'None' }, ...unique];
}
